/* Quantum Waves — commerce behaviours.
 *
 * Everything Shopify-specific that needs JavaScript lives here rather than in
 * quantum.js, which is the design system's own script and is shared byte for
 * byte with the static site. Keeping them apart means a change to the shop
 * never risks the presentation, and vice versa.
 *
 * No framework, no build step. Progressive enhancement throughout: every
 * behaviour below has a working no-JS path underneath it — the add-to-cart
 * forms post normally, the search form submits to /search, the quantity input
 * is a number field. If this file fails to load, the shop still sells.
 */
(function () {
  'use strict';

  var cfg = window.themeSettings || {};
  var routes = cfg.routes || {};
  var strings = cfg.strings || {};

  function announce(msg) {
    var el = document.getElementById('a11y-live');
    if (el) { el.textContent = ''; setTimeout(function () { el.textContent = msg; }, 60); }
  }

  function fetchJSON(url, opts) {
    var controller = new AbortController();
    // A slow cart service must not leave checkout waiting indefinitely.
    // The native cart POST remains the final authority for all request fields.
    var timeout = setTimeout(function () { controller.abort(); }, 8000);
    return fetch(url, Object.assign({ headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }, opts, { signal: controller.signal }))
      .then(function (r) { return r.json().then(function (b) { return r.ok ? b : Promise.reject(b); }); })
      .finally(function () { clearTimeout(timeout); });
  }

  /* ---------------------------------------------------------------- focus */
  /* A dialog that cannot be escaped by Tab is a dialog that traps a keyboard
   * user in the page behind it. Every drawer here borrows this. */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var lastFocused = null;

  function trapFocus(container) {
    if (container.__trap) return;
    lastFocused = document.activeElement;
    var nodes = container.querySelectorAll(FOCUSABLE);
    if (nodes.length) nodes[0].focus();
    container.__trap = function (e) {
      if (e.key !== 'Tab') return;
      var list = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (n) {
        return n.offsetParent !== null;
      });
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', container.__trap);
  }

  function releaseFocus(container) {
    if (container.__trap) container.removeEventListener('keydown', container.__trap);
    container.__trap = null;
    if (lastFocused && lastFocused.focus) lastFocused.focus();
    lastFocused = null;
  }

  /* ----------------------------------------------------------- cart drawer */
  var drawer = document.querySelector('[data-cart-drawer]');

  function openDrawer() {
    if (!drawer) return;
    drawer.setAttribute('data-open', 'true');
    drawer.removeAttribute('hidden');
    document.documentElement.style.overflow = 'hidden';
    trapFocus(drawer);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('data-open', 'false');
    document.documentElement.style.overflow = '';
    releaseFocus(drawer);
    setTimeout(function () {
      if (drawer.getAttribute('data-open') === 'false') drawer.setAttribute('hidden', '');
    }, 400);
  }

  if (drawer) {
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); closeDrawer(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') closeDrawer();
    });
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-cart-open]');
    if (opener && drawer && cfg.cartType === 'drawer') { e.preventDefault(); refreshCart().then(openDrawer).catch(function () { window.location.assign(routes.cart); }); }
  });

  /* ------------------------------------------------------------- cart data */
  function setCartCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      if (n === 0) el.setAttribute('data-empty', 'true'); else el.removeAttribute('data-empty');
    });
  }

  function refreshCart() {
    // Section Rendering API: ask Shopify to re-render just the drawer's own
    // section and swap it in. That keeps the markup authoritative in Liquid
    // instead of duplicating it as JS templates that drift.
    return flushPule().then(function () {
      return fetch(routes.cart + '?section_id=cart-drawer', { headers: { Accept: 'text/html' } });
    })
      .then(function (r) { if (!r.ok) throw new Error('Cart refresh failed'); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-body]');
        var here = document.querySelector('[data-cart-drawer-body]');
        if (!fresh || !here) throw new Error('Cart drawer missing');
        var focused = here.contains(document.activeElement) ? document.activeElement : null;
        var focusName = focused && (focused.name || focused.getAttribute('data-line-qty'));
        here.innerHTML = fresh.innerHTML;
        initCommerce(here);
        if (focused) {
          var replacement = Array.from(here.querySelectorAll('input, textarea, button')).find(function (el) { return focusName && (el.name === focusName || el.getAttribute('data-line-qty') === focusName); });
          if (replacement) replacement.focus();
          else if (drawer) drawer.querySelector('[data-drawer-close]').focus();
        }
        return fetchJSON(routes.cartJs);
      })
      .then(function (cart) { setCartCount(cart.item_count); return cart; });
  }

  /* ------------------------------------------------------------ add to cart */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    if (cfg.cartType === 'page') return;              // let it post and land on /cart

    e.preventDefault();
    var added = false;
    var slot = form.querySelector('[data-form-error]');
    if (slot) { slot.hidden = true; slot.textContent = ''; }
    var btn = form.querySelector('[type="submit"]');
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }

    fetch(routes.cartAdd, { method: 'POST', headers: { Accept: 'application/javascript' }, body: new FormData(form) })
      .then(function (r) { return r.json().then(function (b) { return r.ok ? b : Promise.reject(b); }); })
      .then(function (item) {
        added = true;
        announce((strings.addedToCart || 'Added to cart') + ': ' + (item.product_title || ''));
        return refreshCart();
      })
      .then(function () { if (cfg.cartType === 'drawer') openDrawer(); })
      .catch(function (err) {
        var msg = added ? (strings.cartRefreshError || 'Added to your cart. Open the cart page to continue.') : ((err && err.description) || strings.cartError || 'Something went wrong.');
        announce(msg);
        var slot = form.querySelector('[data-form-error]');
        if (!slot) { slot = document.createElement('p'); slot.setAttribute('data-form-error', ''); slot.setAttribute('role', 'alert'); form.appendChild(slot); }
        if (slot) { slot.textContent = msg; slot.hidden = false; }
      })
      .finally(function () { if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; } });
  });

  /* ------------------------------------------------------- line item change */
  document.addEventListener('click', function (e) {
    var step = e.target.closest('[data-qty-step]');
    if (step) {
      e.preventDefault();
      var wrap = step.closest('[data-qty]');
      var input = wrap && wrap.querySelector('input[type="number"]');
      if (!input) return;
      var delta = step.getAttribute('data-qty-step') === 'up' ? 1 : -1;
      var min = parseInt(input.min || '0', 10);
      var next = Math.max(min, (parseInt(input.value, 10) || 0) + delta);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var remove = e.target.closest('[data-line-remove]');
    if (remove) { e.preventDefault(); changeLine(remove.getAttribute('data-line-remove'), 0); }
  });

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.matches('[data-line-qty]')) return;
    changeLine(input.getAttribute('data-line-qty'), parseInt(input.value, 10) || 0);
  });

  var cartChangeChain = Promise.resolve();
  function cartError(root) {
    var msg = strings.cartError || 'Something went wrong. Please try again.';
    announce(msg);
    if (!root) return;
    var slot = root.querySelector('[data-cart-error]');
    if (!slot) { slot = document.createElement('p'); slot.setAttribute('data-cart-error', ''); slot.setAttribute('role', 'alert'); root.prepend(slot); }
    slot.textContent = msg;
  }
  function changeLine(key, quantity) {
    var root = document.querySelector('[data-cart-body]') || document.querySelector('[data-cart-drawer-body]');
    if (root) root.setAttribute('aria-busy', 'true');
    cartChangeChain = cartChangeChain.catch(function () {}).then(function () {
      return flushPule();
    }).then(function () {
      return fetchJSON(routes.cartChange, { method: 'POST', body: JSON.stringify({ id: key, quantity: Math.max(0, Math.floor(quantity)) }) });
    }).then(function (cart) {
      setCartCount(cart.item_count);
      // Capture anything typed while the quantity request was in flight.
      return flushPule().then(function () {
        if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
        return refreshCart();
      });
    }).catch(function () { cartError(root); })
      .finally(function () { if (root) root.removeAttribute('aria-busy'); });
    return cartChangeChain;
  }

  function initCommerce(root) {
  /* -------------------------------------------------------- variant picker */
  /* Only runs on a product with more than one variant. Single-variant products
   * — which is every programme today — never enter this code path. */
  root.querySelectorAll('[data-variants]').forEach(function (picker) {
    if (picker.dataset.ready) return;
    picker.dataset.ready = 'true';
    var data;
    try { data = JSON.parse(picker.querySelector('[data-variant-json]').textContent); } catch (_) { return; }
    var form = document.querySelector(picker.getAttribute('data-variants'));
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    idInput.disabled = false;
    var section = picker.closest('.shopify-section') || form.parentElement;

    picker.addEventListener('change', function () {
      var chosen = Array.prototype.map.call(picker.querySelectorAll('select, input:checked'), function (i) { return i.value; });
      var match = data.find(function (v) { return v.options.every(function (o, i) { return o === chosen[i]; }); });

      var priceEl = section.querySelector('[data-variant-price]');
      var btn = form.querySelector('[type="submit"]');

      if (!match) {
        idInput.disabled = true;
        if (btn) { btn.disabled = true; btn.textContent = strings.soldOut || 'Unavailable'; }
        return;
      }
      idInput.disabled = !match.available;
      idInput.value = match.id;
      if (priceEl) priceEl.textContent = match.price_label;
      var compare = section.querySelector('[data-variant-compare]');
      if (compare) { compare.textContent = match.compare_label || ''; compare.hidden = !match.compare_label; }
      if (btn) { btn.disabled = !match.available; btn.textContent = match.available ? ((btn.getAttribute('data-label') || strings.addToCart || 'Add to cart') + ' — ' + match.price_label) : (strings.soldOut || 'Unavailable'); }

      // Keep the address bar honest, so a copied link opens the same variant.
      if (history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        history.replaceState({}, '', url.toString());
      }

      if (match.featured_media) {
        var gallery = section.querySelector('[data-gallery]');
        var target = gallery && gallery.querySelector('[data-media-id="' + match.featured_media.id + '"]');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    });
  });

  /* -------------------------------------------------------- media gallery */
  /* Only present on a product with more than one piece of media. The thumbnail
   * marker follows whatever is actually on screen, so a swipe and a click end
   * up in the same state. */
  root.querySelectorAll('[data-gallery]').forEach(function (g) {
    if (g.dataset.ready) return;
    g.dataset.ready = 'true';
    var slides = g.querySelector('[data-gallery-slides]');
    var thumbs = g.querySelectorAll('[data-gallery-thumb]');
    if (!slides || !thumbs.length) return;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var id = entry.target.getAttribute('data-media-id');
          thumbs.forEach(function (t) {
            if (t.getAttribute('data-gallery-thumb') === id) t.setAttribute('aria-current', 'true');
            else t.removeAttribute('aria-current');
          });
        });
      }, { root: slides, threshold: 0.6 });
      slides.querySelectorAll('.gallery__slide').forEach(function (s) { io.observe(s); });
      g.__cleanup = function () { io.disconnect(); };
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        var id = t.getAttribute('data-gallery-thumb');
        var target = slides.querySelector('[data-media-id="' + id + '"]');
        if (target) slides.scrollTo({ left: target.offsetLeft - slides.offsetLeft, behavior: 'smooth' });
      });
    });
  });

  /* ------------------------------------------------------ predictive search */
  var searchForms = root.querySelectorAll('[data-predictive-search]');
  searchForms.forEach(function (form) {
    if (!cfg.predictiveSearch || form.dataset.ready) return;
    form.dataset.ready = 'true';
    var input = form.querySelector('input[type="search"]');
    var panel = form.querySelector('[data-search-results]');
    if (!input || !panel) return;
    var timer, controller;

    function close() { panel.hidden = true; input.setAttribute('aria-expanded', 'false'); }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      if (controller) controller.abort();
      var q = input.value.trim();
      if (q.length < 2) { close(); return; }
      timer = setTimeout(function () {
        if (controller) controller.abort();
        controller = new AbortController();
        var types = [];
        if (form.dataset.products !== 'false') types.push('product');
        if (form.dataset.pages !== 'false') types.push('page');
        if (form.dataset.articles === 'true') types.push('article');
        var url = routes.predictiveSearch + '?q=' + encodeURIComponent(q) +
                  '&resources[type]=' + types.join(',') +
                  '&resources[limit]=6&section_id=predictive-search';
        fetch(url, { signal: controller.signal })
          .then(function (r) { if (!r.ok) throw new Error('Search unavailable'); return r.text(); })
          .then(function (html) {
            if (input.value.trim() !== q || !form.isConnected) return;
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var results = doc.querySelector('[data-search-results-inner]');
            panel.innerHTML = results ? results.innerHTML : '';
            panel.hidden = false;
            input.setAttribute('aria-expanded', 'true');
            announce(panel.textContent.trim());
          })
          .catch(function (err) { if (err.name !== 'AbortError') close(); });
      }, 220);
    });

    form.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); input.focus(); return; }
      if (panel.hidden || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      var links = Array.from(panel.querySelectorAll('a[href]'));
      if (!links.length) return;
      e.preventDefault();
      var index = links.indexOf(document.activeElement);
      links[(index + (e.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length].focus();
    });
    form.addEventListener('focusout', function (e) { if (!form.contains(e.relatedTarget)) close(); });
    form.__cleanup = function () { clearTimeout(timer); if (controller) controller.abort(); };
  });

  root.querySelectorAll('[data-pule]').forEach(function (scope) { syncPule(scope, false); });
  root.querySelectorAll('[data-hide-with-js]').forEach(function (b) { b.hidden = true; });
  }
  document.addEventListener('shopify:section:load', function (e) { initCommerce(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-gallery], [data-predictive-search]').forEach(function (el) { if (el.__cleanup) el.__cleanup(); });
  });

  /* ------------------------------------------------------- search / drawers */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-panel-toggle]');
    if (!t) return;
    e.preventDefault();
    var panel = document.querySelector(t.getAttribute('data-panel-toggle'));
    if (!panel) return;
    var open = panel.getAttribute('data-open') !== 'true';
    panel.setAttribute('data-open', open ? 'true' : 'false');
    document.querySelectorAll('[data-panel-toggle]').forEach(function (button) { if (button.getAttribute('data-panel-toggle') === t.getAttribute('data-panel-toggle')) button.setAttribute('aria-expanded', String(open)); });
    if (open) { panel.removeAttribute('hidden'); trapFocus(panel); }
    else { releaseFocus(panel); panel.hidden = true; }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.panel-drawer[data-open="true"]').forEach(function (panel) {
      panel.setAttribute('data-open', 'false'); panel.hidden = true; releaseFocus(panel);
      document.querySelectorAll('[data-panel-toggle]').forEach(function (button) { button.setAttribute('aria-expanded', 'false'); });
    });
  });

  /* -------------------------------------------------------- pule request */
  var puleTimer;
  var pendingPule;
  var pendingNote;
  var puleSaveChain = Promise.resolve();

  function syncPule(scope, clearUnselected) {
    scope.querySelectorAll('.pule-block').forEach(function (block) {
      var toggle = block.querySelector('input[type="checkbox"]');
      var selected = !!(toggle && toggle.checked);
      block.querySelectorAll('.pule-fields input, .pule-fields textarea').forEach(function (field) {
        field.required = selected && field.hasAttribute('data-pule-required');
        if (!selected && clearUnselected) field.value = '';
      });
    });
  }

  function mirrorPule(scope) {
    var fields = Array.from(scope.querySelectorAll('[name^="attributes["]'));
    document.querySelectorAll('[data-pule]').forEach(function (other) {
      if (other === scope) return;
      other.querySelectorAll('[name^="attributes["]').forEach(function (field) {
        var source = fields.find(function (candidate) {
          return candidate.name === field.name && candidate.type === field.type;
        });
        if (!source) return;
        if (field.type === 'checkbox') field.checked = source.checked;
        else field.value = source.value;
      });
      syncPule(other, true);
    });
  }

  function puleAttributes(scope) {
    var attrs = {};
    scope.querySelectorAll('[name^="attributes["]').forEach(function (field) {
      var key = field.name.slice('attributes['.length, -1);
      if (field.type === 'checkbox') {
        if (field.checked) attrs[key] = field.value;
        else if (!(key in attrs)) attrs[key] = '';
      } else {
        attrs[key] = field.value;
      }
    });
    return attrs;
  }

  function flushPule() {
    clearTimeout(puleTimer);
    if (pendingPule || pendingNote !== undefined) {
      var payload = {};
      if (pendingPule) payload.attributes = puleAttributes(pendingPule);
      if (pendingNote !== undefined) payload.note = pendingNote;
      pendingPule = null;
      pendingNote = undefined;
      // Serialize writes so an older autosave cannot replace a newer request.
      puleSaveChain = puleSaveChain.catch(function () {}).then(function () {
        return fetchJSON(routes.cartUpdate, { method: 'POST', body: JSON.stringify(payload) });
      }).then(function () { announce(strings.puleSaved || ''); });
    }
    return puleSaveChain;
  }

  function queuePule(scope, ms) {
    syncPule(scope, true);
    mirrorPule(scope);
    pendingPule = scope;
    clearTimeout(puleTimer);
    puleTimer = setTimeout(function () {
      flushPule().catch(function () { /* The cart form still posts every field. */ });
    }, ms);
  }

  document.addEventListener('input', function (e) {
    if (!e.target.matches('textarea[name="note"]')) return;
    pendingNote = e.target.value;
    document.querySelectorAll('textarea[name="note"]').forEach(function (note) { note.value = pendingNote; });
    clearTimeout(puleTimer);
    puleTimer = setTimeout(function () { flushPule().catch(function () {}); }, 900);
  });

  document.addEventListener('change', function (e) {
    var scope = e.target.closest && e.target.closest('[data-pule]');
    if (scope) queuePule(scope, 400);
  });

  document.addEventListener('input', function (e) {
    var scope = e.target.closest && e.target.closest('[data-pule]');
    if (scope && e.target.matches('textarea, input[type="text"], input[type="date"], input[type="time"]')) {
      queuePule(scope, 900);
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    var scope = form.querySelector('[data-pule]');
    if (!scope) return;
    if (form.__puleReady) { form.__puleReady = false; return; }
    e.preventDefault();
    if (form.__puleSending) return;

    syncPule(scope, true);
    var submitter = e.submitter || form.querySelector('[name="checkout"]');
    var checkingOut = submitter && submitter.name === 'checkout';
    if (checkingOut) {
      scope.querySelectorAll('[data-pule-required]').forEach(function (field) {
        if (field.required) field.value = field.value.trim();
      });
      var invalid = scope.querySelector(':invalid');
      if (invalid) {
        var details = scope.closest('details');
        if (details) details.open = true;
        invalid.focus();
        invalid.reportValidity();
        return;
      }
    }

    mirrorPule(scope);
    pendingPule = scope;
    form.__puleSending = true;
    if (submitter) submitter.setAttribute('aria-busy', 'true');
    // Finish pending autosaves before the native POST sends the final values.
    cartChangeChain.then(flushPule).catch(function () {}).then(function () {
      form.__puleSending = false;
      form.__puleReady = true;
      if (submitter) submitter.removeAttribute('aria-busy');
      form.requestSubmit(submitter);
    });
  });

  /* --------------------------------------------------------- localization */
  /* The forms work without this — each has a submit button. With JS the button
   * is redundant, so it is removed and the form submits on change. */
  document.addEventListener('change', function (e) { if (e.target.matches('select[data-auto-submit]') && e.target.form) e.target.form.requestSubmit(); });

  /* --------------------------------------------------------------- share */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-share-copy]');
    if (!btn) return;
    e.preventDefault();
    var url = btn.getAttribute('data-share-copy');
    var label = btn.querySelector('[data-share-label]');
    var revert = label && label.textContent;
    var done = function () {
      if (label) { label.textContent = strings.copied || 'Link copied'; setTimeout(function () { label.textContent = revert; }, 2000); }
      announce(strings.copied || 'Link copied');
    };
    if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(url).then(done).catch(function () { announce(strings.cartError || 'Could not copy the link.'); });
    else {
      var t = document.createElement('textarea');
      t.value = url; t.setAttribute('readonly', ''); t.style.position = 'absolute'; t.style.left = '-9999px';
      document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); done(); } finally { document.body.removeChild(t); }
    }
  });

  // Filter forms use their native GET submission, including pagination and empty states.
  initCommerce(document);
})();
